/**
 * Sort comparator functions for common sorting operations.
 */

type DateValue = string | Date | null | undefined;
type SortDirection = 'ASC' | 'DESC';

/**
 * Creates a date comparator function for sorting.
 * Handles null/undefined values by placing them at the end.
 *
 * @param direction - 'ASC' for oldest first, 'DESC' for newest first
 * @param path - Optional property key to extract date value from objects
 * @returns Comparator function for use with Array.prototype.sort()
 *
 * @example
 * // Sort date values directly
 * dates.sort(dateComparator('DESC'));
 *
 * @example
 * // Sort objects by a date property
 * goals.sort(dateComparator('ASC', 'createdAt'));
 */
export function dateComparator(
  direction: SortDirection,
): (a: DateValue, b: DateValue) => number;
export function dateComparator<T, K extends keyof T>(
  direction: SortDirection,
  path: K,
): (a: T, b: T) => number;
export function dateComparator<T, K extends keyof T>(
  direction: SortDirection,
  path?: K,
): (a: T | DateValue, b: T | DateValue) => number {
  return (a, b) => {
    const dateA: DateValue =
      path != null ? ((a as T)[path] as DateValue) : (a as DateValue);
    const dateB: DateValue =
      path != null ? ((b as T)[path] as DateValue) : (b as DateValue);

    // Handle null/undefined - push them to the end
    if (dateA == null && dateB == null) return 0;
    if (dateA == null) return 1;
    if (dateB == null) return -1;

    const timeA = new Date(dateA).getTime();
    const timeB = new Date(dateB).getTime();

    return direction === 'ASC' ? timeA - timeB : timeB - timeA;
  };
}
